<template>
  <div v-click-outside="close" class="c-vacancy-select">
    <!-- Trigger -->
    <div
      class="trigger"
      :class="{ 'is-open': isOpen, 'has-value': selected.length > 0 }"
      @click="toggle"
    >
      <span class="trigger-text">
        {{ selected.length === 0 ? placeholder : triggerLabel }}
      </span>
      <svg class="icon-search" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    </div>

    <!-- Dropdown -->
    <div v-if="isOpen" class="dropdown">
      <!-- Search -->
      <div class="search-wrap">
        <input
          ref="searchInput"
          v-model="searchQuery"
          type="text"
          class="search-input"
          :placeholder="placeholder"
        />
        <svg class="icon-search" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
      </div>

      <!-- Status filter -->
      <div class="status-filter">
        <label
          v-for="s in statuses"
          :key="s.value"
          class="radio-label"
          :class="{ active: statusFilter === s.value }"
        >
          <input
            v-model="statusFilter"
            type="radio"
            :value="s.value"
            class="radio-input"
          />
          <span class="radio-circle"></span>
          <span class="radio-text">{{ s.label }}</span>
        </label>
      </div>

      <!-- City filter -->
      <div class="city-wrap">
        <select v-model="cityFilter" class="city-select">
          <option value="">Фильтр по городу</option>
          <option v-for="city in cities" :key="city" :value="city">{{ city.label }}</option>
        </select>
        <svg class="icon-chevron" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>

      <!-- Vacancy list -->
      <div class="list">
        <label
          v-for="vacancy in filteredVacancies"
          :key="vacancy.value"
          class="list-item"
          :class="{ 'is-checked': isSelected(vacancy.value) }"
        >
          <input
            v-model="selected"
            type="checkbox"
            :value="vacancy.value"
            class="checkbox-input"
          />
          <span class="item-text">{{ vacancy.label }}</span>
        </label>

        <div v-if="filteredVacancies.length === 0" class="empty">
          Ничего не найдено
        </div>
      </div>

      <!-- Select all -->
      <div class="footer">
        <button class="select-all-btn" @click="toggleAll">
          {{ isAllSelected ? 'Снять все' : 'Выбрать все' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CVacancySelect',

  directives: {
    clickOutside: {
      bind(el, binding) {
        el._clickOutsideHandler = (event) => {
          if (!el.contains(event.target)) {
            binding.value()
          }
        }
        document.addEventListener('click', el._clickOutsideHandler)
      },
      unbind(el) {
        document.removeEventListener('click', el._clickOutsideHandler)
      },
    },
  },

  props: {
    // Array of { id, name, status: 'active'|'archive', city }
    vacancies: {
      type: Array,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: 'Вакансия',
    },
    value: {
      type: Array,
      default: () => [],
    },
    cities: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      isOpen: false,
      searchQuery: '',
      statusFilter: 'PUBLISHED',
      cityFilter: '',
      selected: [ ...this.value, ],
      statuses: [
        { label: 'Все', value: 'all', },
        { label: 'Активные', value: 'PUBLISHED', },
        { label: 'Архивные', value: 'ARCHIVED', },
      ],
    }
  },

  computed: {

    filteredVacancies() {
      return this.vacancies.filter((v) => {
        const matchStatus =
            this.statusFilter === 'all' || v.status === this.statusFilter
        const matchCity = !this.cityFilter || v.cityId === this.cityFilter.value
        const matchSearch =
            !this.searchQuery ||
            v.name.toLowerCase().includes(this.searchQuery.toLowerCase())
        return matchStatus && matchCity && matchSearch
      })
    },

    isAllSelected() {
      return (
        this.filteredVacancies.length > 0 &&
          this.filteredVacancies.every((v) => this.selected.includes(v.value))
      )
    },

    triggerLabel() {
      if (this.selected.length === 1) {
        const v = this.vacancies.find((v) => v.value === this.selected[0])
        return v ? v.label : `${this.selected.length} выбрано`
      }
      return `${this.selected.length} выбрано`
    },
  },

  watch: {
    selected(val) {
      this.$emit('input', val)
    },
    value(val) {
      this.selected = [ ...val, ]
    },
  },

  methods: {
    toggle() {
      this.isOpen = !this.isOpen
      if (this.isOpen) {
        this.$nextTick(() => {
          this.$refs.searchInput && this.$refs.searchInput.focus()
        })
      }
    },

    close() {
      this.isOpen = false
    },

    isSelected(id) {
      return this.selected.includes(id)
    },

    toggleAll() {
      if (this.isAllSelected) {
        const filteredIds = this.filteredVacancies.map((v) => v.value)
        this.selected = this.selected.filter((id) => !filteredIds.includes(id))
      } else {
        const filteredIds = this.filteredVacancies.map((v) => v.value)
        const merged = new Set([ ...this.selected, ...filteredIds, ])
        this.selected = [ ...merged, ]
      }
    },
  },
}
</script>

<style lang="scss" scoped>
$green: #1D9E75;
$border: #e0e0e0;
$radius: 12px;
$text: #222;
$muted: #888;

.c-vacancy-select {
  position: relative;
  width: 100%;
  font-size: 16px;
  font-family: inherit;
}

/* ── Trigger ── */
.trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border: 1.5px solid $border;
  border-radius: $radius;
  cursor: pointer;
  background: #fff;
  transition: border-color 0.2s;
  gap: 8px;

  &.is-open,
  &:hover {
    border-color: $green;
  }

  &.has-value .trigger-text {
    color: $text;
  }

  .trigger-text {
    flex: 1;
    color: $muted;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .icon-search {
    flex-shrink: 0;
    color: $muted;
  }
}

/* ── Dropdown ── */
.dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 100;
  background: #fff;
  border: 1.5px solid $border;
  border-radius: $radius;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.10);
  overflow: hidden;
}

/* ── Search ── */
.search-wrap {
  position: relative;
  padding: 10px 12px;
  border-bottom: 1px solid $border;

  .search-input {
    width: 100%;
    border: 1.5px solid $green;
    border-radius: 8px;
    padding: 8px 36px 8px 12px;
    font-size: 15px;
    outline: none;
    box-sizing: border-box;
    color: $text;

    &::placeholder {
      color: $muted;
    }
  }

  .icon-search {
    position: absolute;
    right: 22px;
    top: 50%;
    transform: translateY(-50%);
    color: $muted;
    pointer-events: none;
  }
}

/* ── Status radio ── */
.status-filter {
  display: flex;
  gap: 20px;
  padding: 10px 14px;
  border-bottom: 1px solid $border;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;

  .radio-input {
    display: none;
  }

  .radio-circle {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 2px solid $border;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: border-color 0.15s;

    &::after {
      content: '';
      width: 9px;
      height: 9px;
      border-radius: 50%;
      background: transparent;
      transition: background 0.15s;
    }
  }

  &.active .radio-circle {
    border-color: $green;

    &::after {
      background: $green;
    }
  }

  .radio-text {
    font-size: 15px;
    color: $text;
  }
}

/* ── City ── */
.city-wrap {
  position: relative;
  padding: 8px 12px;
  border-bottom: 1px solid $border;

  .city-select {
    width: 100%;
    appearance: none;
    border: 1.5px solid $border;
    border-radius: 8px;
    padding: 8px 36px 8px 12px;
    font-size: 15px;
    color: $muted;
    background: #fff;
    cursor: pointer;
    outline: none;
    box-sizing: border-box;

    &:focus {
      border-color: $green;
    }
  }

  .icon-chevron {
    position: absolute;
    right: 22px;
    top: 50%;
    transform: translateY(-50%);
    color: $muted;
    pointer-events: none;
  }
}

/* ── List ── */
.list {
  max-height: 280px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: $border;
    border-radius: 4px;
  }
}

.list-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  gap: 10px;
  transition: background 0.12s;

  &:hover {
    background: #f6f6f6;
  }

  &.is-checked .item-text {
    color: $text;
  }

  .checkbox-input {
    display: none;
  }

  // Custom checkbox via ::before on item-text
  &::before {
    content: '';
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    border-radius: 4px;
    border: 2px solid $border;
    background: #fff;
    transition: background 0.15s, border-color 0.15s;
    box-sizing: border-box;
  }

  &.is-checked::before {
    background: $green;
    border-color: $green;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: center;
  }

  .item-text {
    flex: 1;
    font-size: 15px;
    color: $text;
    line-height: 1.35;
  }
}

.empty {
  padding: 20px 16px;
  text-align: center;
  color: $muted;
  font-size: 14px;
}

/* ── Footer ── */
.footer {
  padding: 10px 16px;
  border-top: 1px solid $border;
}

.select-all-btn {
  background: none;
  border: none;
  color: $green;
  font-size: 15px;
  cursor: pointer;
  padding: 0;
  font-family: inherit;

  &:hover {
    text-decoration: underline;
  }
}
</style>
